/**
 * 提示音（任务 18c2b，notifyModel 的 sound 开关）。
 *
 * **刻意不用音频文件**：一个 mp3/ogg 就是一次额外 HTTP + 一份体积，而提示音只是
 * 两声短「叮」。用 WebAudio 现场合成 = 零请求、零字节（PROMPT 最少请求原则）。
 * 系统通知那条通道的声音由系统放（`NotificationOptions.silent`，18b/18c2b），
 * 这里只服务窗口内提示条。
 *
 * 自动播放策略：AudioContext 在用户交互前是 suspended。提示条只在**前台且窗口
 * 有焦点**时出（notifyDecision），这时用户基本已经点过页面；真被拦住就是没声，
 * 不报错、不重试、也不去纠缠 resume 的结果。
 */

/** 测试注入用的最小面（只用到这几个成员，不依赖完整 AudioContext 类型） */
export interface MinimalAudioContext {
  readonly state: string;
  readonly currentTime: number;
  readonly destination: AudioNode;
  resume(): Promise<void>;
  createOscillator(): OscillatorNode;
  createGain(): GainNode;
}

export type AudioContextFactory = () => MinimalAudioContext | null;

/** 两声「叮」：频率（Hz）与相对起点（秒） */
const BEEPS: ReadonlyArray<{ freq: number; at: number }> = [
  { freq: 880, at: 0 },
  { freq: 1174.7, at: 0.09 },
];
const BEEP_DUR = 0.08;
/** 峰值音量：提示音不该盖过别人说话/视频，取一个克制的值 */
const BEEP_GAIN = 0.07;

/** 浏览器默认工厂（不支持 WebAudio = null → 调用方静默） */
export function browserAudioContextFactory(): AudioContextFactory {
  let ctx: MinimalAudioContext | null = null;
  return () => {
    if (ctx) return ctx;
    const Ctor = (
      window as unknown as { AudioContext?: new () => MinimalAudioContext }
    ).AudioContext;
    if (typeof Ctor !== 'function') return null;
    try {
      // 同一页只建一个：AudioContext 数量有上限，每条通知建一个很快就爆
      ctx = new Ctor();
    } catch {
      ctx = null;
    }
    return ctx;
  };
}

/**
 * 返回一个「放一次提示音」的函数。任何一步失败都静默——没声音不是错误。
 */
export function createNotifySound(factory: AudioContextFactory): () => void {
  return () => {
    let ctx: MinimalAudioContext | null = null;
    try {
      ctx = factory();
    } catch {
      return;
    }
    if (!ctx) return;
    try {
      if (ctx.state === 'suspended') void ctx.resume().catch(() => undefined);
      const t0 = ctx.currentTime;
      for (const beep of BEEPS) {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.value = beep.freq;
        const start = t0 + beep.at;
        // 线性淡出：方波式硬切会有「啪」的爆音
        gain.gain.setValueAtTime(BEEP_GAIN, start);
        gain.gain.linearRampToValueAtTime(0, start + BEEP_DUR);
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start(start);
        osc.stop(start + BEEP_DUR);
      }
    } catch {
      // 节点创建/调度失败（上下文已关闭等）：静默
    }
  };
}
